/**
 * This module exports the convenience function [[register]] to help you define
 * model mappings and view registrations succinctly.
 */

/****/

import { mapper, ModelConstructor, ModelFactoryFunction } from '../model/Mapper';
import { ViewRegistration, ViewRegistrationMap } from '../view/View';


/**
 * This convenience method helps to configure model mappings and view registrations via a fluent API.
 *
 * @param callback This callback function will receive a `builder` object which provides various methods:
 *
 *  * Specify how raw data ("JSON") is mapped to class instances using **`.model(...)`**.
 *    (This will invoke the global `mapper.toModel.add` behind the curtains.)
 *
 *  * Register views using `.model(...)`**`.view(...)`** (if you want to tie a view to a certain
 *    type of model class) or using **`.view(...)`** (if you want e.g. named views).
 *
 * @returns An object that can be queried for the following:
 *
 *  * **`.viewRegistrations`**. Note that these are not automatically registered. You need to pass these
 *    registrations on either to a `View` instance (via the constructor) or to `scan`. (You *can* also
 *    register these globally using `register` (from the `core/view/View` module), however that method
 *    has been marked deprecated.)
 *
 * @example
 *```typescript
 * const {viewRegistrations} = register(({model, view}) => {
 *     model('App\\Heading', Heading).view(HeadingView);
 * });
 *
 * scan(document.body, viewRegistrations);
 *```
 *
 * Then in your templates (assuming that there is a `heading` data property which was
 * mapped from a `{ modelName: "App\Heading" }` JSON object), you can resolve `heading` to a `HeadingView`
 * as follows:
 *
 *```html
 * <div view="[[heading.constructor]]" data="[[heading]]"></div>
 *```
 */
export default function register( callback:( builder:RegistrationsBuilder ) => void ):Registrations {
	const builder = new RegistrationsBuilderImpl();

	// This weird hack is needed such that destructuring of `builder`
	// won't lose the `this` binding to `builder`:
	callback({
		model: builder.model.bind(builder),
		view: builder.view.bind(builder),
	});

	return builder;
}

/**
 * The registrations produced (and returned) by [[register]].
 */
interface Registrations {

	/**
	 * The view registrations defined. Note that these are not automatically registered for you.
	 * You will need to pass them forward to e.g. [["core/view/View".View]]'s constructor, or to
	 * the [["core/view/View".scan]] method.
	 */
	viewRegistrations:ViewRegistrationMap;
}

/**
 * Provides various methods that can be used to specify model mappings and view registrations.
 * An instance of this type will be passed to the `callback` that you pass to [[register]].
 */
interface RegistrationsBuilder {

	/**
	 * Use this method to create an association between a model class and a view registration.
	 * @param constructor The model class that should be associated with a view registration.
	 *
	 * @example
	 *
	 *```typescript
	 * class Image extends Model { ... }
	 * class ImageView extends View<Image> { ... }
	 * register(({ model }) => model(Image).view(ImageView));
	 *```
	 *
	 * After this, you will be able to resolve `Image` models to `ImageView` instances
	 * in your templates as follows:
	 *
	 *```html
	 * <div view="[[image]]" data="[[image]]"></div>
	 *```
	 */
	model<T>( constructor:ModelConstructor<T> ):ModelViewAssociationBuilder<T>;

	/**
	 * Use this method to map raw data with the given `modelName` to a model class,
	 * and optionally create an association between that model class and a view registration.
	 *
	 * @param name The `modelName` identifying data to be mapped to the model class.
	 * @param constructor The model class that should be mapped to.
	 *
	 * @example
	 *
	 *```typescript
	 * class Image extends Model { ... }
	 * register(({ model }) => model('App\\Image', Image));
	 *```
	 *
	 * or:
	 *
	 *```typescript
	 * class Image extends Model { ... }
	 * class ImageView extends View<Image> { ... }
	 * register(({ model }) => model('App\\Image', Image).view(ImageView));
	 *```
	 *
	 * This second form will allow you to resolve `Image` models to `ImageView` instances
	 * in your templates as follows:
	 *
	 *```html
	 * <div view="[[image]]" data="[[image]]"></div>
	 *```
	 */
	model<T>( name:string, constructor:ModelConstructor<T> ):ModelViewAssociationBuilder<T>;

	/**
	 * Use this method to map raw data with the given `modelName` to a model class,
	 * and optionally create an association between that model class and a view registration.
	 *
	 * @param name The `modelName` identifying data to be mapped to the model class.
	 * @param factory A closure function that should produce an model class (`T`) instance from
	 *   the raw data object it receives.
	 *
	 * @example
	 *
	 *```typescript
	 * class Image extends Model { ... }
	 * register(({ model }) => model('App\\Image', dto => new Image(dto, ...)));
	 *```
	 */
	model<T>( name:string, factory:ModelFactoryFunction<T> ):void;

	/**
	 * Use this method to create a view registration under the given `key`. The key can be
	 * anything: usually, you'll want to specify a string or a class constructor.
	 *
	 * @param key The key that identifies the view registration.
	 * @param registration The view registration.
	 *
	 * @example
	 *
	 *```typescript
	 * class App extends Model { ... }
	 * class AppView extends View<App> { ... }
	 * register(({ view }) => view('App', AppView));
	 *```
	 *
	 * or:
	 *
	 *```typescript
	 * class App extends Model { ... }
	 * register(({ view }) => view('App', { template: `...`, getData: () => new App(...) }));
	 *```
	 *
	 * Both of the examples above will allow you to "instantiate" an `App` view in your templates
	 * as follows:
	 *
	 *```html
	 * <div view="App"></div>
	 *```
	 *
	 * Note that the `key` need not be a string. You could also specify a model class instead:
	 *
	 *```typescript
	 * class App extends Model { ... }
	 * class AppView extends View<App> { ... }
	 * register(({ view }) => view(App, AppView));
	 *```
	 *
	 * This is equivalent to the preferable `model(App).view(AppView)` and allows you to then
	 * resolve an `App` model to an `AppView` in your templates as follows:
	 *
	 *```html
	 * <div view="[[app]]" data="[[app]]">
	 *```
	 */
	view<TModel = unknown>( key:unknown, registration:ViewRegistration<TModel> ):void;
}

/** @ignore */
interface ModelViewAssociationBuilder<TModel> {
	view( registration:ViewRegistration<TModel> ):void;
}

/** @ignore */
class RegistrationsBuilderImpl implements RegistrationsBuilder, Registrations {

	public readonly viewRegistrations:ViewRegistrationMap;

	public constructor() {
		this.viewRegistrations = new ViewRegistrationMap();
	}

	public model<T>( nameOrConstructor:string|ModelConstructor<T>, constructorOrFactory?:ModelConstructor<T>|ModelFactoryFunction<T> ) {

		const viewRegistrations = this.viewRegistrations;

		if( typeof nameOrConstructor === 'string' ) {
			if( isModelConstructor( constructorOrFactory ) ) {
				mapper.toModel.add( nameOrConstructor, dto => new constructorOrFactory( dto ) );
				return {
					view( registration:ViewRegistration<T> ):void {
						viewRegistrations.set( constructorOrFactory, registration );
					}
				};
			}
			else {
				mapper.toModel.add( nameOrConstructor, constructorOrFactory );
				return;
			}
		}
		else {
			return {
				view( registration:ViewRegistration<T> ):void {
					viewRegistrations.set( nameOrConstructor, registration );
				}
			};
		}

		function isModelConstructor( fn:Function ): fn is ModelConstructor<T> {
			return !!fn.length;
		}
	}

	public view( key:unknown, registration:ViewRegistration ): void {
		this.viewRegistrations.set( key, registration );
	}
}
